import { Accordion, Code, Group, List, SimpleGrid, Stack, Text } from '@mantine/core';

/**
 * Phase 35 (Sketch 019, Variante A4): Die aufklappbare Erklaerung ueber der Ablegeflaeche --
 * "Wie die Berichte gelesen werden". Hervorgegangen aus
 * `docs/kunde/2026-09-03-emissionsberichte-vorlagenaenderungen.html` (an die Messstelle
 * adressiert), seit dem 15.09.2026 aber am tatsaechlichen Verhalten des Imports ausgerichtet statt
 * am Kundendokument: Die Spalten sagen, was beim Import PASSIERT, damit der Bearbeiter weiss, ob er
 * die Word-Datei anpassen muss. Das Kundendokument nennt als dritten Ankerpunkt eine
 * Uebersichtstabelle und eine Verknuepfung mit Nachtragsschreiben -- beides wertet der Import nicht
 * aus.
 *
 * Belege je Spalte: Hinweise ohne Abbruch aus `EmissionReportDocxParser::logMissingFields()` und
 * `EmissionReportMeasurementMapper` (`missing_date`, `oxygen_reference_not_confirmed`); Abbruch
 * ("nicht lesbar") aus dem Einheitenabgleich, dem Spaltenwaechter und `no_portal_table` im
 * `EmissionReportImportUploadProcessor`. Die Buendelung beschreibt
 * `EmissionReportMeasurementMapper::buildGrid()`/`assign()`/`resolveDate()`.
 *
 * Der Text liegt bewusst als DATEN in `RULE_COLUMNS` und `BUNDLING_POINTS` und nicht als
 * verschachteltes JSX -- eine kuenftige Ergaenzung ist dann eine Zeile im Array statt eines
 * Eingriffs in die Darstellung.
 */

/** Stand, an dem der Inhalt zuletzt gegen den Import geprueft wurde. */
const RULES_AS_OF = '28.09.2026';

interface RuleColumn {
  /** Was beim Import passiert -- die drei Ueberschriften sind der Kern der Dreiteilung. */
  title: string;
  /** Mantine-Farbe der Kopfzeile: gruen = folgenlos, gelb = Hinweis, rot = nicht lesbar. */
  color: string;
  items: string[];
}

const RULE_COLUMNS: RuleColumn[] = [
  {
    title: 'Frei änderbar',
    color: 'green',
    items: [
      'Reihenfolge und Nummerierung der Tabellen',
      'Schreibweise der Beschriftung außerhalb der Klammer',
      'Zusatzspalten wie Max., Mittel, BG, FBW – rechts neben den Messspalten',
      'Zusätzliche Stoffe und Tabellen',
    ],
  },
  {
    title: 'Feld bleibt leer, Hinweis in der Vorschau',
    color: 'yellow',
    items: [
      'Kennung in der Klammer geändert oder entfallen',
      'Tabelle ohne „Messung Nr.“ oder als Bild',
      'Summenzeile umbenannt',
      'Unbekannter Marker für fehlende Werte',
      'Kein Datum lesbar – im Formular vor dem Einreichen nachtragen',
      // Geschützte Leerzeichen: „10 Vol.-% O₂“ darf in der schmalen Ansicht nicht zwischen Zahl und Einheit umbrechen.
      'Anmerkung unter der Tabelle bestätigt 10 Vol.-% O₂ nicht – übernommen werden trotzdem 10 Vol.-% O₂',
      'Massenstrom in unbekannter Einheit (erlaubt: kg/h, g/h, mg/h, µg/h) – Mittelwert bleibt leer',
    ],
  },
  {
    title: 'Bericht nicht lesbar',
    color: 'red',
    items: [
      'Andere oder fehlende Einheit bei einer Konzentration',
      'Zusatzspalte vor oder zwischen den Messspalten',
      'Keine einzige Tabelle wird erkannt',
      'PDF statt Word-Datei – wird gar nicht angenommen',
    ],
  },
];

const BUNDLING_POINTS: string[] = [
  'Ein Wert wird über Datum und Betriebszustand seiner Probe einer Messung zugeordnet – nie über die Nummer in „Messung Nr.“. Kurze Proben wie HF und HCl laufen mehrmals am Tag, lange wie PCDD/F oder PCB einmal, und jede Tabelle zählt bei 1 los.',
  'Die Stoffgruppe mit den meisten Proben legt Zahl, Datum und Betriebszustand der Messungen fest. Jeder weitere Wert landet in der ersten Messung mit gleichem Tag und Betriebszustand, deren Feld noch frei ist – lange Proben stehen deshalb etwa auf Messung 1, 3 und 5.',
  'Passt eine Probe zu keiner dieser Messungen, legt der Import eine zusätzliche Messung an, statt den Wert zu verwerfen.',
  '„VB“ und „Verbundbetrieb“, „DB“ und „Direktbetrieb“ gelten als gleich. Fehlt einem Datum das Jahr, wird es ergänzt, wenn der Bericht denselben Tag an anderer Stelle vollständig nennt.',
];

const MASS_FLOW_POINTS: string[] = [
  'Gelesen werden die Massenströme derselben Stoffe wie bei den Konzentrationen: die Zeile „…-Massenstrom“ bei HCl, HF, Formaldehyd und Benzol, sonst die eigene Tabelle „Massenströme …“ mit derselben Kennung in Klammern. Benzo(a)pyren nur aus der eigenen Tabelle.',
  'Quecksilber, Staub, SO₂ und NH₃ werden auch als Massenstrom nicht übernommen – sie sind kontinuierlich gemessen.',
  'Die Einheit im Bericht gilt. Kilogramm, Gramm, Milligramm und Mikrogramm pro Stunde werden in die Einheit des Rechenblatts umgerechnet.',
  'Je Stoff entstehen zwei Mittelwerte, getrennt nach Verbund- und Direktbetrieb, jeweils aus den eigenen Messungen des Stoffs. Werte unter der Bestimmungsgrenze zählen mit dem halben Wert, n.b., n.n. und b zählen nicht.',
  'Ein zweiter Bericht für denselben Ofen und dasselbe Jahr ersetzt die Mittelwerte genau der Stoffe, die er mitbringt; die übrigen bleiben stehen.',
];

export function ImportRulesPanel() {
  return (
    <Accordion
      variant="contained"
      chevronPosition="left"
      styles={{ item: { backgroundColor: 'var(--mantine-color-white)' } }}
    >
      <Accordion.Item value="rules">
        <Accordion.Control>
          <Group gap="xs" wrap="nowrap" justify="space-between">
            <Text size="sm">
              <Text span fw={600}>
                Wie die Berichte gelesen werden
              </Text>{' '}
              <Text span c="dimmed">
                — und was sich daran ändern darf, ohne dass der Import bricht
              </Text>
            </Text>
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              Stand {RULES_AS_OF}
            </Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Der Import sucht die Tabellen nicht über ihre Nummer, sondern über{' '}
              <Text span fw={600} c="var(--mantine-color-text)">
                drei Ankerpunkte
              </Text>
              : die Kennung in Klammern in der Beschriftung (etwa <Code>(HF)</Code>,{' '}
              <Code>(PCDD/F)</Code>), die Kopfzeile <Code>Messung Nr.</Code> und die Bezeichnung
              der Wertzeile. Was daran nicht rührt, darf sich ändern.
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              {RULE_COLUMNS.map((column) => (
                <Stack key={column.title} gap={6}>
                  <Text size="sm" fw={600} c={column.color}>
                    {column.title}
                  </Text>
                  <List size="sm" spacing={4} withPadding>
                    {column.items.map((item) => (
                      <List.Item key={item}>{item}</List.Item>
                    ))}
                  </List>
                </Stack>
              ))}
            </SimpleGrid>
            <Stack gap={6}>
              <Text size="sm" fw={600}>
                Wie die Messungen gebündelt werden
              </Text>
              <List size="sm" spacing={4} withPadding>
                {BUNDLING_POINTS.map((point) => (
                  <List.Item key={point}>{point}</List.Item>
                ))}
              </List>
            </Stack>
            <Stack gap={6}>
              <Text size="sm" fw={600}>
                Massenströme für die Frachtenberechnung
              </Text>
              <List size="sm" spacing={4} withPadding>
                {MASS_FLOW_POINTS.map((point) => (
                  <List.Item key={point}>{point}</List.Item>
                ))}
              </List>
            </Stack>
            <Text size="sm" c="dimmed">
              Weicht ein Bericht davon ab: die Word-Datei anpassen, den Eintrag verwerfen und den
              Bericht erneut ablegen.
            </Text>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
